import React, { useCallback, useMemo, useContext, useState, useEffect } from 'react';
import { IStyle } from 'Styles';
import './style.scss';
import { GroupDTO, IGroupDTO } from 'business/models/Models';
import { handleTableFilter } from './baseGroup';
import { UserGroupContext } from '../../Licensing/userSetup';
import { RMModal } from 'components/modal';
import { getNewId, showAlertSnackBar } from 'utils/Global';
import EAlertType from 'enums/EAlertType';
import ESnackBarPosition from 'enums/ESnackBarPosition';
import HeaderBar from 'components/main/Setup/CrystalContainer/HeaderBar';
import RMTable from 'components/customComponents/Controls/RMTable';

interface Props {
  onSelectedGroupChange: (group: any) => void;
  selectedGroups: GroupDTO[];
  setSelectedGroups: React.Dispatch<React.SetStateAction<any[]>>;
  tableContainerStyle?: React.CSSProperties;
  setEditId: React.Dispatch<React.SetStateAction<number>>;
  editId: number;
}

const getTableColumns: object[] = [
  {
    accessor: 'name',
    Filter: handleTableFilter('Group Name'),
  },
  {
    accessor: 'groupAdmin',
    Filter: handleTableFilter('Group Admin'),
  },
];

const initialModalState = {
  heading: '',
  content: null,
  okText: '',
  isConfirmationModal: false,
  onOkPress: null,
};

const editProtectedGroupNames = ['Administrators', 'AllRockMakerUsers'];
const Group = (props: Props) => {
  const { onSelectedGroupChange, selectedGroups, setSelectedGroups, setEditId, editId } =
    props;
  const { tempState, setTempState, rowError, setRowError } = useContext(UserGroupContext);
  const [showModal, setShowModal] = useState(false);
  const [selectedRows, setSelectedRows] = useState<number[]>([]);
  const [currentModal, setCurrentModal] = useState(initialModalState);

  const selectedGroupIds = useMemo(
    () => selectedGroups.map((u) => u.id),
    [selectedGroups],
  );

  useEffect(() => {
    if (selectedGroupIds.length) {
      const indices = [];
      tempState.groups.forEach((group, index) => {
        if (selectedGroupIds.includes(group.id)) {
          indices.push(index);
        }
      });
      if (indices.length) {
        setSelectedRows(indices);
      }
    }
  }, [selectedGroupIds, tempState]);

  const groupData = useMemo(
    () =>
      tempState.groups?.map((group) => ({
        name:
          editId !== group.id ? (
            group.name
          ) : (
            <input
              id="sections-group-data-input"
              type="text"
              className="rounded-sm bg-white p-1 w-full"
              onChange={(e) => onCellChange(e, group, 'name', 'text')}
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
              }}
              value={group.name}
            />
          ),
        id: group.id,
        groupAdmin: group.groupAdmins.length > 0 ? group.groupAdmins[0].name : '-',
        groupAdmins: group.groupAdmins,
        users: group.users,
      })) || [],
    [tempState, selectedRows, editId],
  );

  const shouldAllowGroupDelete = useCallback(() => {
    if (selectedGroups.some((user) => editProtectedGroupNames.includes(user.name))) {
      return [false, `You cannot delete the ${editProtectedGroupNames.toString()}`];
    }

    return [true, ''];
  }, [selectedGroups]);

  const handleAdd = useCallback(() => {
    const newGroupId = getNewId();

    const newGroup: GroupDTO = new GroupDTO({
      id: newGroupId,
      name: '',
      groupAdmins: [],
      users: [],
    });

    const groupsList = [...tempState.groups];
    groupsList.push(newGroup);
    setEditId(newGroupId);
    setSelectedGroups([newGroup]);
    setSelectedRows([0]);
    setTempState({ ...tempState, groups: groupsList.sort((a, b) => a.id - b.id) });
  }, [selectedGroups, tempState, selectedRows]);

  const handleEdit = useCallback(() => {
    setEditId(selectedGroupIds[0]);
  }, [selectedGroups, tempState, selectedRows]);

  const handleDelete = useCallback(() => {
    const [allowGroupDelete, allowGroupDeleteMessage] = shouldAllowGroupDelete();
    if (!allowGroupDelete) {
      setCurrentModal(initialModalState);
      setShowModal(false);
      return showAlertSnackBar(
        EAlertType.ERROR,
        ESnackBarPosition.TOP,
        allowGroupDeleteMessage as string,
      );
    }

    const groups: IGroupDTO[] = tempState.groups.filter(
      (group) => !selectedGroupIds.includes(group.id),
    );
    setSelectedRows([]);
    setRowError((prv) => ({ ...prv, groups: {} }));
    setTempState((prv) => ({ ...prv, groups }));
    setCurrentModal(initialModalState);
    setShowModal(false);
  }, [selectedGroups]);

  const handleDeleteConfirmation = useCallback(() => {
    const contentText = selectedGroups.some((g) => g.isImagerGroup === true)
      ? 'Some User groups have entry in Imager Quota. Deleting these groups will also delete its imager Quota entry. Are you sure you want to delete selected group(s)?'
      : 'Are you sure you want to delete selected group(s)?';
    setCurrentModal((prv) => ({
      ...prv,
      heading: 'Delete Group(s)',
      okText: 'Delete',
      content: contentText,
      isConfirmationModal: true,
      onOkPress: handleDelete,
    }));
    setShowModal(true);
  }, [selectedGroups]);

  const handleSelectionChange = useCallback(
    (rowIndices: any) => {
      setSelectedRows(rowIndices);
      onSelectedGroupChange(rowIndices);
      setEditId(0);
    },
    [onSelectedGroupChange],
  );

  const onCellChange = (e, value, key, type) => {
    e.stopPropagation();
    e.preventDefault();
    const tempGroups = [...tempState.groups];

    if (editProtectedGroupNames.includes(value.name)) {
      showAlertSnackBar(
        EAlertType.ERROR,
        ESnackBarPosition.TOP,
        'You can not edit this group',
      );
      return;
    }

    if (type === 'text') {
      for (const grp of tempGroups) {
        if (grp.id === value.id) {
          grp[key] = e.target.value;
          break;
        }
      }
    }
    setTempState({ ...tempState, groups: tempGroups });
  };

  const handleOnGroupRowCLick = (row) => {
    setSelectedRows([row.index]);
    onSelectedGroupChange([row.index]);
    setEditId(0);
  };

  return (
    <>
      <HeaderBar
        title={'Groups'}
        onAdd={handleAdd}
        onEdit={handleEdit}
        onDelete={handleDeleteConfirmation}
        enableEditDelete={selectedGroups.length > 0}
        disableEdit={selectedGroups.length !== 1}
      />
      <div
        id="sections-group-table-container"
        className="user-setup-table table-main-container h-[85%]"
        style={{ ...styles.tableContainer }}
      >
        <RMTable
          columns={getTableColumns}
          data={groupData}
          onCheckboxSelectionChange={handleSelectionChange}
          selectedIds={selectedRows}
          onRowClicked={handleOnGroupRowCLick}
          rowExtraInfo={rowError['groups']}
        />
      </div>
      <RMModal
        id="sections-group-modal"
        setShowModal={setShowModal}
        showModal={showModal}
        body={currentModal.content}
        heading={currentModal.heading}
        showButtons={currentModal.isConfirmationModal}
        onOk={currentModal.onOkPress}
      />
    </>
  );
};

const styles: IStyle = {
  headerContainer: {
    padding: 20,
    paddingTop: 8,
  },
  headerTitle: {
    fontWeight: 'bold',
  },
};

export default Group;
